import { useQuery } from '@tanstack/react-query';
import apiClient from '../../services/api';
import type { FieldConfig, ModuleConfig } from './types';

// Entry form settings (Administration -> Entry Form Settings): per form and
// field, make an optional field required, hide it, rename it, change its
// help text or give it a starting value. Fields the server itself requires
// (or whose rule depends on other choices) can't be made optional or hidden.

export type FormValue = string | number | boolean;

export interface FieldRule {
  required?: boolean | null;
  hidden?: boolean;
  label?: string | null;
  help?: string | null;
  default?: FormValue | null;
}

export interface FormRule {
  fields?: Record<string, FieldRule>;
  options?: Record<string, FormValue>;
}

export interface FormSettings {
  forms: Record<string, FormRule>;
}

export const formSettingsApi = {
  async get(): Promise<FormSettings> {
    const { data } = await apiClient.get<FormSettings>('/system-settings/forms');
    return data;
  },
  async save(settings: FormSettings): Promise<FormSettings> {
    const { data } = await apiClient.put<FormSettings>('/system-settings/forms', settings);
    return data;
  },
};

export function useFormSettings() {
  return useQuery({ queryKey: ['form-settings'], queryFn: formSettingsApi.get, staleTime: 60_000 });
}

/** One form's rules (empty while loading). */
const NO_RULE: FormRule = {};

export function useFormRule(formId: string): FormRule {
  const { data } = useFormSettings();
  return data?.forms[formId] ?? NO_RULE;
}

/** What the admin may change on a field:
 *  - free: optional now - can be made required or hidden;
 *  - always: the server needs it - stays required and visible;
 *  - depends: required only for some choices - left as the form has it;
 *  - fixed: shown for information only. */
export type FieldLock = 'free' | 'always' | 'depends' | 'fixed';

export function fieldLock(f: Pick<FieldConfig, 'type' | 'required' | 'autoCalculated' | 'systemGenerated'>): FieldLock {
  if (f.type === 'display' || f.autoCalculated || f.systemGenerated) return 'fixed';
  if (f.required === true) return 'always';
  if (typeof f.required === 'function') return 'depends';
  return 'free';
}

/** Field types a starting value can be set for. */
export function canHaveDefault(f: Pick<FieldConfig, 'type' | 'optionsKey' | 'staticOptions'>): boolean {
  if (f.type === 'select') return !!f.staticOptions || typeof f.optionsKey === 'string';
  return ['text', 'textarea', 'number', 'decimal', 'checkbox'].includes(f.type);
}

/** The form with the admin's rules applied. */
export function applyFormRule<T extends object>(config: ModuleConfig<T>, rule: FormRule): ModuleConfig<T> {
  const rules = rule.fields ?? {};
  if (Object.keys(rules).length === 0) return config;

  const fields = config.fields.map((f) => {
    const r = rules[f.name];
    if (!r) return f;
    const out: FieldConfig<T> = { ...f };
    if (r.label) out.label = r.label;
    if (r.help) out.helperText = r.help;
    if (fieldLock(f) === 'free') {
      if (r.hidden) {
        out.visibleWhen = () => false;
        out.required = false;
      } else if (r.required) {
        // Required only while it's actually on screen.
        out.required = (v) => !f.visibleWhen || f.visibleWhen(v);
        out.optional = false;
      }
    }
    return out;
  });

  const defaults = Object.fromEntries(
    Object.entries(rules)
      .filter(([name, r]) => r.default !== undefined && r.default !== null && r.default !== '' && config.fields.some((f) => f.name === name && canHaveDefault(f)))
      .map(([name, r]) => [name, r.default]),
  );
  const base = config.defaultValues;
  const defaultValues = Object.keys(defaults).length
    ? () => ({ ...(typeof base === 'function' ? base() : base ?? {}), ...defaults }) as Partial<T>
    : base;

  return { ...config, fields, defaultValues };
}
